"use client";

import { useCallback, useState, type SyntheticEvent } from "react";
import Image from "next/image";
import type { ImageDataType } from "@/shared/types";

const LIGHTBOX_MAX_WIDTH = 1360;

type FrameSize = {
  width: number;
  height: number;
};

function getLightboxBounds(): { maxWidth: number; maxHeight: number } {
  const isDesktop = window.matchMedia("(min-width: 768px)").matches;
  const headerHeightValue = getComputedStyle(document.documentElement)
    .getPropertyValue("--header-height")
    .trim();
  const headerHeight = Number.parseFloat(headerHeightValue) || 0;
  const verticalReserve = isDesktop ? 136 : 160;
  const horizontalReserve = 32;

  return {
    maxWidth: Math.min(
      LIGHTBOX_MAX_WIDTH,
      window.innerWidth - horizontalReserve
    ),
    maxHeight: window.innerHeight - headerHeight - verticalReserve,
  };
}

function getFrameSize(
  naturalWidth: number,
  naturalHeight: number
): FrameSize {
  const { maxWidth, maxHeight } = getLightboxBounds();
  const scale = Math.min(
    maxWidth / naturalWidth,
    maxHeight / naturalHeight
  );

  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
}

interface LightboxImageFrameProps {
  image: ImageDataType;
  priority?: boolean;
}

export function LightboxImageFrame({
  image,
  priority = false,
}: LightboxImageFrameProps) {
  const [frameSize, setFrameSize] = useState<FrameSize | null>(null);

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      setFrameSize(getFrameSize(img.naturalWidth, img.naturalHeight));
    },
    []
  );

  const placeholderBounds = getLightboxBounds();

  return (
    <div
      className="relative shrink-0"
      style={{
        width: frameSize?.width ?? placeholderBounds.maxWidth,
        height: frameSize?.height ?? placeholderBounds.maxHeight,
      }}
    >
      <Image
        src={image.src}
        alt={image.alt || ""}
        title={image.title ?? undefined}
        fill
        className="object-contain"
        priority={priority}
        sizes="(max-width: 1439px) 90vw, 1360px"
        onLoad={handleImageLoad}
      />
    </div>
  );
}
